/**
 * User-facing error hints.
 *
 * Maps a failed API request to an actionable next step, printed after the raw
 * error message so the user gets guidance instead of only a status code + JSON
 * body. Returns null when there's nothing useful to add (the raw message stands
 * on its own). Wired into the single top-level catch in `index.ts`.
 */

import type { RunSessionErrorDetails } from '@wayai/core/contracts';
import { ApiError, dataErrorMessage } from './api-client.js';
import { isNetworkError } from './network-error.js';

/** Pull the human-facing `message` out of a JSON error body, falling back to the raw text. */
export function extractApiMessage(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.body) as { message?: string; error?: string };
      if (parsed.message) return parsed.message;
      if (parsed.error) return parsed.error;
    } catch {
      // body wasn't JSON — fall through to the raw text
    }
    return err.body || err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

/**
 * The machine-readable `error` code from a JSON error body, or null. Callers
 * discriminate on THIS, never on the bare HTTP status: `/evals` returns 409 for
 * several unrelated conditions (a fixture held by another session, a full lease
 * ledger, a session still draining, a non-empty scenario set) whose right next
 * step differs — only one of them is worth waiting out.
 */
export function extractApiErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  try {
    const parsed = JSON.parse(err.body) as { error?: unknown };
    return typeof parsed.error === 'string' ? parsed.error : null;
  } catch {
    return null;
  }
}

/**
 * The backend's forwarded `Retry-After`, in seconds, from `details`. Present on
 * Rekor's transient seed-lease backpressure (gh #3751), where it is the server's
 * own floor for the next attempt — a waiter that backs off faster is ignoring
 * the only capacity signal on the wire.
 *
 * Returns null unless the value is a finite non-negative number, so a malformed
 * body leaves the caller on its own backoff rather than on a garbage delay.
 */
export function extractApiRetryAfterSeconds(err: unknown): number | null {
  if (!(err instanceof ApiError)) return null;
  try {
    // Typed against the contract, so renaming the field backend-side breaks this
    // read at compile time instead of silently dropping the server's floor. The
    // value is still re-validated: `body` is wire data, and the contract type
    // describes what the backend MEANS to send, not what actually arrived.
    const parsed = JSON.parse(err.body) as { details?: RunSessionErrorDetails };
    const seconds: unknown = parsed?.details?.retry_after_seconds;
    return typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
  } catch {
    return null;
  }
}

export function friendlyHint(err: unknown): string | null {
  // A host-network failure (offline / DNS / connection reset) surfaces as a bare
  // `fetch failed` with no status — give the user something to act on (gh #2933).
  if (isNetworkError(err)) {
    return "Couldn't reach WayAI — check your network connection and try again.";
  }
  if (!(err instanceof ApiError)) return null;

  // Free-plan gate (e.g. `wayai publish`/`sync` — production is paid-only). The
  // backend throws a ValidationError carrying `code: 'FREE_PLAN_RESTRICTION'`;
  // match the body so the hint is legible regardless of the mapped HTTP status.
  if (err.body.includes('FREE_PLAN_RESTRICTION')) {
    return 'Publishing to production requires a paid plan. Upgrade your organization to publish or sync.';
  }

  // Deliberately NO seed-lease hints here (gh #3745). Every command that can
  // receive one — `run-eval`, `eval session delete` — matches the code and
  // `process.exit(1)` itself, so an error carrying one never reaches this
  // top-level catch; a hint added here would be dead code. The backend already
  // writes the actionable sentence, and the one piece of CLI-only guidance
  // (`--no-queue`) is printed by `run-eval` where it is reachable.

  // The Data surface answers with its own `{ error: { message } }` envelope, so
  // its failures otherwise reach the user as a raw transport line with a JSON
  // body glued on. Placed above the status ladder because the surface's own
  // sentence is more specific than anything a status code can say — including
  // the 404 that means the backend serves no Data proxy at all.
  const dataMessage = dataErrorMessage(err);
  if (dataMessage) return dataMessage;

  const { status } = err;
  if (status === 401) return 'Your session may have expired. Run `wayai login` to re-authenticate.';
  if (status === 402) return 'Quota exceeded for your plan. Review usage or upgrade your plan.';
  if (status === 403) return 'Access denied. Your token may lack permission for this hub or resource.';
  if (status === 429) return 'Rate limited. Wait a moment and try again.';
  if (status >= 500) return 'WayAI server error — likely temporary. Try again shortly.';
  return null;
}
